import { ReactNode, useState } from "react";
import { Button, Container, Grid2, Typography, Alert, Tooltip } from "@mui/material";
import { useUnifiedCheckout } from "@shop/hooks/useUnifiedCheckout";
import { useQuery } from "@apollo/client";
import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { useShopSlice } from "@store/slices/shop";
import CheckoutConfirmationDialog from "./CheckoutConfirmationDialog";
import { showSuccessNotification } from "@/utils/errorHandling";
import { useLocation } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";
import { hasAtLeastOneSeatSelected } from "@shop/utils/seatValidation";

interface BaseShopLayoutProps {
    title: string;
    /**
     * Optional description that can be either a simple string or a fully
     * fledged React node. If a React node is provided, it will be rendered
     * directly, allowing the caller full control over styling and layout.
     */
    description: ReactNode;
    buttonLable?: string;
    buttonOnClick?: () => void;
    buttonDisabled?: boolean;
    children: ReactNode;
    /**
     * When true, BaseShopLayout will not create its own scroll container 
     * and will instead allow the outer layout (SettingsLayout or AppLayout) 
     * to manage scrolling. Useful when embedded in settings where only a 
     * single global scrollbar is desired.
     * 
     * Auto-detects based on route: true for /settings paths, false otherwise.
     */
    useOuterScroll?: boolean;
}

const BaseShopLayout = ({ title, description, buttonLable, buttonOnClick, buttonDisabled, children, useOuterScroll }: BaseShopLayoutProps) => {
    const { pathname } = useLocation();
    
    // Auto-detect if not explicitly set: true for settings paths, false otherwise
    const shouldUseOuterScroll = useOuterScroll ?? pathname.startsWith('/settings');
    // Einheitlicher Checkout - viel einfacher!
    const handleCheckout = useUnifiedCheckout();
    const { checkoutStatus, selectedProducts, products } = useShopSlice();
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

    const isLoading = checkoutStatus === "loading";
    const hasSelectedProducts = selectedProducts.length > 0;
    
    // Prüfen ob wir auf der Onboarding-Shop-Seite sind
    const isOnboardingShop = pathname === RouteTo.SHOP;
    
    // Für Onboarding prüfen, ob mindestens ein Sitz ausgewählt ist
    const hasRequiredSeats = isOnboardingShop ? hasAtLeastOneSeatSelected(selectedProducts, products) : true;
    
    // Bestimmen ob Button deaktiviert werden soll
    const isButtonDisabled = buttonDisabled ?? (isLoading || !hasSelectedProducts || (isOnboardingShop && !hasRequiredSeats));
    
    // Tooltip-Nachricht für Onboarding wenn keine Sitze ausgewählt sind aber andere Produkte ausgewählt sind
    const getTooltipMessage = () => {
        if (isOnboardingShop && hasSelectedProducts && !hasRequiredSeats) {
            return "Mindestens 1 Sitz muss ausgewählt sein";
        }
        return "";
    };

    const handleOpenConfirmationDialog = () => {
        setIsConfirmationDialogOpen(true);
    };

    const handleCloseConfirmationDialog = () => {
        setIsConfirmationDialogOpen(false);
    };

    const handleConfirmCheckout = () => {
        handleCheckout();
    };

    const tooltipMessage = getTooltipMessage();
    const showTooltip = !!tooltipMessage;

    const checkoutButton = (
        <Button
            variant="contained"
            size="large"
            onClick={buttonOnClick ?? handleOpenConfirmationDialog}
            disabled={isButtonDisabled}
            sx={{ py: 2, px: 8 }}
        >
            {isLoading ? "In Bearbeitung..." : (buttonLable ?? "Zum Checkout")}
        </Button>
    );

    return (
        <Container
            maxWidth={pathname === RouteTo.SHOP ? false : "lg"}
            sx={{
                // Conditional height and overflow based on scroll mode
                height: shouldUseOuterScroll ? undefined : availableViewportHeight,
                overflow: shouldUseOuterScroll ? "visible" : "auto",
                py: 4,
            }}
        >
            <Grid2 container spacing={4}>
                <Grid2 size={12}>
                    <Typography variant="h3" component="h1" gutterBottom>
                        {title}
                    </Typography>
                    {typeof description === "string" ? (
                        <Typography variant="body1" color="text.secondary">
                            {description}
                        </Typography>
                    ) : (
                        // If description is a React element, render it directly to allow custom styling/layout
                        <>{description}</>
                    )}
                </Grid2>

                <Grid2 size={12}>{children}</Grid2>

                <Grid2 size={12} container justifyContent="center">
                    {showTooltip ? (
                        <Tooltip title={tooltipMessage} arrow placement="top">
                            <span>
                                {checkoutButton}
                            </span>
                        </Tooltip>
                    ) : (
                        checkoutButton
                    )}
                </Grid2>
            </Grid2>

            <CheckoutConfirmationDialog open={isConfirmationDialogOpen} onClose={handleCloseConfirmationDialog} onConfirm={handleConfirmCheckout} isLoading={isLoading} />
        </Container>
    );
};

export default BaseShopLayout;
